import React, {
  ReactNode,
  FunctionComponent,
  useCallback,
  useContext,
} from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { isRouteMulti, RouteItem, RouteMulti } from '../../../routes/RouteItem';
import { SidebarProps } from './Sidebar.types';
import { SidebarItem } from './SidebarItem';
import { RoutesStore } from '../../../context';
import { FixedLogo } from '../Logo';

const Container = styled.div`
  position: fixed;
  flex-shrink: 0;

  flex-direction: column;
  display: flex;

  top: 0;
  bottom: 0;
  width: auto;
  overflow-y: auto;

  z-index: 2;

  background-color: ${props => props.theme.colors.primaryColor};

  @media (max-width: 1000px) {
    width: 50px;
  }

  @media (min-width: 1000px) {
    width: 17rem;
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 70%;
  max-height: 1000px;

  @media (max-width: 1000px) {
    padding: 0.5rem 0.5rem 0.5rem 1.5rem;
  }

  @media (min-width: 1000px) {
    padding: 1.5rem 1.5rem 1.5rem 2.5rem;
  }
`;

export const Sidebar: FunctionComponent<SidebarProps> = props => {
  const { pathname } = useLocation();
  const { sidebarRoutes } = useContext(RoutesStore);
  const logo = props.logo;

  const renderItems = useCallback(
    (routes: (RouteItem | RouteMulti)[]) => {
      const items: ReactNode[] = [];

      routes.forEach((route, key) => {
        if (isRouteMulti(route)) {
          // handle many paths that go to one component
          route.paths!.forEach((path, i) => {
            items.push(
              <SidebarItem
                key={`${key}-${i}`}
                pathname={pathname}
                path={path}
                icon={route.icons![i]}
                title={route.names![i]}
                disabled={!pathname.includes('application')}
              />,
            );
          });
        } else {
          // handle one path that goes to one component
          items.push(
            <SidebarItem
              key={key}
              pathname={pathname}
              path={route.path}
              icon={route.icon}
              title={route.title}
              disabled={!pathname.includes('application')}
            />,
          );
        }
      });

      return items;
    },
    [pathname],
  );

  return (
    <Container data-testid="containerLeft">
      {props.children}
      {!pathname.includes('welcome') && (
        <Items>{renderItems(sidebarRoutes)}</Items>
      )}
      <FixedLogo>
        <img alt="logo" src={logo} />
      </FixedLogo>
    </Container>
  );
};
